import * as React from 'react';
import { Box, Divider, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { withStyles } from '../../settings/styles';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { AppState } from '../../model/reducers';
import { NavigationGroup, NavigationPath, NavigationPaths } from '../../model/navigation/types';
import { UserState } from '../../model/user/types';
import { WithTranslation, withTranslation } from 'react-i18next';

const item = {
    py: '2px',
    px: 3,
    color: 'rgba(255, 255, 255, 0.7)',
    '&:hover, &:focus': {
      bgcolor: 'rgba(255, 255, 255, 0.08)',
    },
};

const itemCategory = {
    boxShadow: '0 -1px 0 rgb(255,255,255,0.1) inset',
    py: 1.5,
    px: 3,
};

interface State {};

interface LocalProps extends WithTranslation
{
    groups: NavigationGroup[];
    pages: NavigationPaths;
    currentPage: NavigationPath;
    open?: boolean;
    mobile?: boolean;
    width: number;
    onChange: any;
    onClose?: any;
}

interface StateProps
{
    user: UserState;
}

interface DispatchProps
{

}

type Props = LocalProps & StateProps & DispatchProps;

class Navigator extends React.Component<Props, State>
{
    // ============================================ //
    //               Member Variables               //
    // ============================================ //

    translation: any;

    // ============================================ //
    //               Routine Functions              //
    // ============================================ //

    constructor(props: any)
    {
        super(props);

        // Init State
        this.state = {
        };
    }

    // ============================================ //
    //               Render Functions               //
    // ============================================ //

    render()
    {
        const { groups, mobile, open, width, onClose } = this.props;
        return (<>
            <Drawer 
                key={"nav-drawer"}
                PaperProps={{ style: { width } }}
                variant={mobile ? "temporary" : "permanent"} 
                sx={mobile ? {} : { display: { sm: 'block', xs: 'none' } }}
                open={open}
                onClose={onClose}
            >
                <List key={"nav-list"} disablePadding>

                    {/** NAV TITLE */}
                    {this.renderNavTitle()}

                    {/** NAV GROUPS */}
                    {groups.map(({ id, title, children }) => this.renderNavGroup(id, title, children))}

                    {this.renderNavLang()}
                </List>
            </Drawer>
            </>);
    }

    renderNavTitle()
    {
        const { t } = this.props;
        return (
            <ListItem key="nav-title" sx={{ ...item, ...itemCategory, flexDirection: 'column' }}>
                <Box key={"nav-title-logo"}>
                    <img alt="" height="85px" width="173px" src="./assets/viewbaselogocitron.png" />
                </Box>
                <Box key={"nav-title-text"} sx={{ fontSize: 22, color: '#ffd631' }}>
                    {t('general.integrations')}
                </Box>
            </ListItem>
        );
    }

    renderNavGroup(id: string, title: string, children: string[])
    {
        const { t } = this.props;
        const pageIds = children.filter(pageId=>this.props.user.isElevatedUser||!this.props.pages[pageId].elevated);
        if(pageIds.length <= 0) return;

        return (
            <Box key={id} sx={{ bgcolor: '#8a5075' }}>

                {/** NAV GROUP TITLE */}
                <ListItemText key={`nav-group-title-${id}`} sx={{ color: '#ffd631', py: 2, px: 3, margin: 0 }}>{t(title)}</ListItemText>

                {/** NAV LINK */}
                {pageIds.map((pageId: string) => this.renderNavLink(pageId))}

                {/** DIVIDER */}
                <Divider key={`nav-group-divider-${id}`} sx={{ mt: 2 }} />

            </Box>
        )
    }

    renderNavLink(pageId: string)
    {
        const { t } = this.props;
        const page = this.props.pages[pageId];
        const active = this.props.currentPage.id === pageId;
        return (
            <ListItem disablePadding key={`nav-link-item-${pageId}`}>
                <ListItemButton key={`nav-link-item-btn-${pageId}`} selected={active} sx={item} onClick={()=>this.props.onChange(pageId)}>
                    <ListItemIcon key={`nav-link-item-icon-${pageId}`}>{<page.Icon/>}</ListItemIcon>
                    <ListItemText key={`nav-link-item-text-${pageId}`}>{t(page.title)}</ListItemText>
                </ListItemButton>
            </ListItem>
        )
    }

    renderNavLang()
    {
        const { t, i18n } = this.props;
        return (<>
            <Box key={'lang'} sx={{ bgcolor: '#8a5075' }}>

                {/** NAV GROUP TITLE */}
                <ListItemText key={`nav-lang-group`} sx={{ color: '#ffd631', py: 2, px: 3, margin: 0 }}>{t('general.languages')}</ListItemText>

                {/* TRANSLATION ENGLISH */}
                <ListItem disablePadding key={"English"}>
                    <ListItemButton key={`nav-link-lang-en`} selected={i18n.language==='en'} sx={item} onClick={()=>{i18n.changeLanguage('en')}}>
                        <ListItemIcon>
                            <img alt="" src="./assets/uk-flag.svg" width="20px" height="20px" style={{margin:"0px 5px 0px 0px"}}/>
                        </ListItemIcon>
                        <ListItemText primary={"English"} style={{marginLeft:"-5px"}} />
                    </ListItemButton>
                </ListItem>

                {/* TRANSLATION SWEDISH */}
                <ListItem disablePadding key={"Svenska"}>
                    <ListItemButton key={`nav-link-lang-sv`} selected={i18n.language==='sv'} sx={item} onClick={()=>{i18n.changeLanguage('sv')}}>
                        <ListItemIcon>
                            <img alt="" src="./assets/swe-flag.svg" width="20px" height="20px" style={{margin:"0px 5px 0px 0px"}}/>
                        </ListItemIcon>
                        <ListItemText primary={"Svenska"} style={{marginLeft:"-5px"}} />
                    </ListItemButton>
                </ListItem>

                {/** DIVIDER */}
                <Divider key={`nav-lang-group-divider`} sx={{ mt: 2 }} />
            </Box>
        </>);
    }

    // ============================================ //
}

// -------------- Redux Props --------------  //

const mapStateToProps = (state: AppState, props: LocalProps): StateProps => ({
    user: state.user.current
});

const mapDispatchToProps = (dispatch: ThunkDispatch<{},{},any>, props: LocalProps): DispatchProps => ({
});

// ----------------- Export -----------------  //

export default withStyles(connect(mapStateToProps, mapDispatchToProps)(withTranslation()(Navigator)));

// ------------------------------------------  //